import { useEffect, useState } from 'react';

import { useJoinWorkspaceInfo } from '~/components/JoinWorkspace/hooks/useJoinWorkspaceInfo';
import { useWorkspaces } from '~/swr-hooks/workspaces/useWorkspaces';

type WorkspaceRedirectState = 'loading' | 'needsRedirect' | 'hasAccessToWorkspace';

/**
 * Decides whether Home should send the user to `/workspace/redirect`.
 *
 * Uses `peek` (any pending invite), not the email-bound consume helper. A pending
 * invite whose `boundEmail` does not match the signed-in account is exactly the
 * unauthorized case that must redirect — bound-email matching gates join/consume,
 * not this mismatch UX.
 */
export const useWorkspaceRedirectState = () => {
  const [workspaceRedirectState, setNeedsWorkspaceRedirect] = useState<WorkspaceRedirectState>('loading');
  const { data: workspaces } = useWorkspaces();
  const { getJoinWorkspaceInfo } = useJoinWorkspaceInfo();

  useEffect(() => {
    if (!workspaces) {
      return;
    }

    const workspaceInfo = getJoinWorkspaceInfo();

    setNeedsWorkspaceRedirect(
      workspaceInfo && !workspaces.find((workspace) => workspace.id === workspaceInfo.id)
        ? 'needsRedirect'
        : 'hasAccessToWorkspace',
    );
  }, [workspaces, getJoinWorkspaceInfo]);

  return { workspaceRedirectState };
};
